import React, { useState, useEffect } from "react";
import { Dialog, CircularProgress } from "@mui/material";
import { X, Edit2, Plus, Trash2, Save, XCircle, Info, Image as ImageIcon, Utensils, Copy } from "lucide-react";
import mealsApi from "../../api/meals";
import useAuth from "../../hooks/useAuth";
import MealRecipeViewer from "./MealRecipeViewer";

interface MealDetailModalProps {
  open: boolean;
  onClose: () => void;
  mealId: string | null;
  onUpdate: () => void;
}

const MealDetailModal: React.FC<MealDetailModalProps> = ({ open, onClose, mealId, onUpdate }) => {
  const { admin } = useAuth();
  const isAdminUser = admin?.role === "admin" || admin?.role === "super-admin";
  
  const [loading, setLoading] = useState(false);
  const [meal, setMeal] = useState<any>(null);
  const [editMode, setEditMode] = useState(false);
  const [portionTab, setPortionTab] = useState<"classic" | "light">("classic");
  const [viewTab, setViewTab] = useState<"recipe" | "ingredients">("recipe");
  
  const [editName, setEditName] = useState("");
  const [editQuantities, setEditQuantities] = useState<Record<string, { value: string; unit: string }>>({});
  const [editKitchenIngredients, setEditKitchenIngredients] = useState<any[]>([]);
  const [editKitchenIngredientsLight, setEditKitchenIngredientsLight] = useState<any[]>([]);

  useEffect(() => {
    if (open && mealId) {
      fetchMealDetails();
      setEditMode(false);
      setPortionTab("classic");
      setViewTab("recipe");
    } else {
      setMeal(null);
    }
  }, [open, mealId]);

  const fetchMealDetails = async () => {
    setLoading(true);
    try {
      const data = await mealsApi.getMealById(mealId!);
      setMeal(data.meal);
      setEditName(data.meal.name);
      resetQuantities(data.meal);
      setEditKitchenIngredients(data.meal.kitchenIngredients || []);
      setEditKitchenIngredientsLight(data.meal.kitchenIngredientsLight || []);
    } catch (error) {
      console.error("Error fetching meal:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetQuantities = (data: any) => {
    setEditQuantities(Object.fromEntries(["quantityClassic", "quantityLight"].map(field => [field, {
      value: data[field]?.value?.toString() || "",
      unit: data[field]?.unit || "g",
    }])));
  };

  const handleSave = async () => {
    const quantities: Record<string, { value: number; unit: string } | null> = {};
    for (const field of ["quantityClassic", "quantityLight"]) {
      const entry = editQuantities[field];
      if (!entry?.value.trim()) {
        quantities[field] = null;
        continue;
      }
      const value = Number(entry.value);
      if (!Number.isFinite(value) || value <= 0 || (entry.unit === "piece" && !Number.isInteger(value))) {
        alert("Enter a positive food quantity. Piece counts must be whole numbers.");
        return;
      }
      quantities[field] = { value, unit: entry.unit };
    }
    setLoading(true);
    try {
      await mealsApi.patchMeal(mealId!, {
        name: editName,
        ...quantities,
        kitchenIngredients: editKitchenIngredients,
        kitchenIngredientsLight: editKitchenIngredientsLight,
      });
      setEditMode(false);
      onUpdate();
      await fetchMealDetails();
    } catch (error) {
      console.error("Error saving meal:", error);
      alert("Failed to save. Check inputs.");
    } finally {
      setLoading(false);
    }
  };

  const currentList = portionTab === "classic" ? editKitchenIngredients : editKitchenIngredientsLight;
  const setCurrentList = portionTab === "classic" ? setEditKitchenIngredients : setEditKitchenIngredientsLight;

  const handleAddIngredient = () => {
    setCurrentList([
      ...currentList,
      { ingredientId: "", displayName: "", quantity: 1, unit: "g", category: "" },
    ]);
  };

  const handleIngredientChange = (index: number, field: string, value: any) => {
    const updated = [...currentList];
    updated[index] = { ...updated[index], [field]: field === "quantity" ? Number(value) : value };
    setCurrentList(updated);
  };

  const handleRemoveIngredient = (index: number) => {
    const updated = currentList.filter((_, i) => i !== index);
    setCurrentList(updated);
  };

  const handleCopyClassicToLight = () => {
    const cloned = editKitchenIngredients.map((ing) => ({ ...ing }));
    setEditKitchenIngredientsLight(cloned);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ style: { borderRadius: '8px', margin: '16px' } }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-white sticky top-0 z-10">
        <h2 className="text-xl font-bold text-gray-900 flex items-center">
          {editMode ? (
            <><Edit2 className="w-5 h-5 mr-3 text-indigo-500" /> Edit Meal</>
          ) : (
            <><Info className="w-5 h-5 mr-3 text-indigo-500" /> Meal Details</>
          )}
        </h2>
        <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors focus:outline-none">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <div className="p-6 bg-gray-50 max-h-[75vh] overflow-y-auto">
        {loading && !meal ? (
          <div className="flex justify-center py-16"><CircularProgress /></div>
        ) : meal ? (
          <div className="space-y-6">
            {/* Top Info Card */}
            <div className={`bg-white rounded-lg p-5 border border-gray-200 flex gap-4 ${viewTab === "recipe" ? "items-start" : "flex-col sm:flex-row"}`}>
              {meal.imageUrl ? (
                <img src={meal.imageUrl} alt={meal.name} className={`${viewTab === "recipe" ? "w-16 h-16" : "w-32 h-32"} shrink-0 object-cover rounded-lg border border-gray-100`} />
              ) : (
                <div className={`${viewTab === "recipe" ? "w-16 h-16" : "w-32 h-32"} shrink-0 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 border border-gray-200`}>
                  <ImageIcon className="w-8 h-8" />
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                {editMode ? (
                  <div className="mb-4">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Meal Name</label>
                    <input 
                      type="text" 
                      value={editName} 
                      onChange={(e) => setEditName(e.target.value)} 
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all text-sm font-medium shadow-sm"
                    />
                  </div>
                ) : (
                  <h3 className={`${viewTab === "recipe" ? "text-lg" : "text-2xl"} font-bold text-gray-900 mb-2 break-words`}>{meal.name}</h3>
                )}
                
                <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-gray-600">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                    {meal.category}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-700 border border-gray-200">
                    S/N: {meal.serialNumber}
                  </span>
                </div>
                
                {viewTab === "ingredients" && <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Public Ingredients</p>
                  <div className="flex flex-wrap gap-2">
                    {meal.ingredients?.map((ing: string, i: number) => (
                      <span key={i} className="px-2.5 py-1 bg-white border border-gray-200 shadow-sm text-gray-700 text-xs font-medium rounded-md">
                        {ing}
                      </span>
                    ))}
                  </div>
                </div>}
              </div>
            </div>

            <section className="bg-white rounded-lg p-5 border border-gray-200">
              <h3 className="text-base font-bold text-gray-900 mb-2">Food quantity per meal</h3>
              <p className="text-sm text-gray-500 mb-4">Food weight or piece count, separate from container capacity. Leave blank if unconfirmed.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(["classic", "light"] as const).map(variant => {
                  const field = variant === "classic" ? "quantityClassic" : "quantityLight";
                  const label = variant === "classic" ? "Classic" : "Light";
                  const quantity = meal[field];
                  const capacity = variant === "classic" ? meal.portionSize : meal.portionSizeLight;
                  return (
                    <div key={variant}>
                      <p className="text-sm font-semibold mb-2">{label}</p>
                      {editMode ? (
                        <div className="flex gap-2">
                          <input aria-label={`${label} food quantity`} type="number" min="0" step={editQuantities[field]?.unit === "piece" ? "1" : "any"}
                            value={editQuantities[field]?.value || ""} placeholder="Not set"
                            onChange={e => setEditQuantities(prev => ({ ...prev, [field]: { ...prev[field], value: e.target.value } }))}
                            className="w-32 border border-gray-300 rounded px-3 py-2" />
                          <select aria-label={`${label} quantity unit`} value={editQuantities[field]?.unit || "g"}
                            onChange={e => setEditQuantities(prev => ({ ...prev, [field]: { ...prev[field], unit: e.target.value } }))}
                            className="border border-gray-300 rounded px-3 py-2">
                            <option value="g">Grams</option><option value="piece">Pieces</option>
                          </select>
                        </div>
                      ) : <p className="text-sm">{quantity ? `${quantity.value} ${quantity.unit === "g" ? "g" : quantity.value === 1 ? "piece" : "pieces"}` : "Not set"}</p>}
                      <p className="text-xs text-gray-500 mt-2">Container / size description: {capacity || "Not set"}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            <div role="tablist" aria-label="Meal information" className="flex border-b border-gray-300 gap-6">
              <button role="tab" aria-selected={viewTab === "recipe"} onClick={() => setViewTab("recipe")} className={`py-3 text-sm font-semibold border-b-2 ${viewTab === "recipe" ? "border-green-700 text-green-800" : "border-transparent text-gray-600"}`}>Recipe</button>
              <button role="tab" aria-selected={viewTab === "ingredients"} onClick={() => setViewTab("ingredients")} className={`py-3 text-sm font-semibold border-b-2 ${viewTab === "ingredients" ? "border-green-700 text-green-800" : "border-transparent text-gray-600"}`}>Raw Ingredients</button>
            </div>
            {viewTab === "recipe" && <MealRecipeViewer key={meal._id} recipe={meal.recipeDocument} mealName={meal.name}
              mealId={meal._id} canEdit={isAdminUser} onSaved={recipe => { setMeal((previous: any) => ({ ...previous, recipeDocument: recipe })); onUpdate(); }} />}
            {/* Kitchen Ingredients Section */}
            {viewTab === "ingredients" && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50/50">
                <div className="flex items-center space-x-3">
                  <h3 className="text-base font-bold text-gray-900">Raw Ingredients</h3>
                  {/* Portion Switcher */}
                  <div className="inline-flex rounded-lg p-0.5 bg-gray-200 border border-gray-300">
                    <button
                      type="button"
                      onClick={() => setPortionTab("classic")}
                      className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                        portionTab === "classic"
                          ? "bg-purple-600 text-white shadow-sm"
                          : "text-gray-700 hover:text-gray-900"
                      }`}
                    >
                      Classic ({editMode ? editKitchenIngredients.length : (meal.kitchenIngredients?.length || 0)})
                    </button>
                    <button
                      type="button"
                      onClick={() => setPortionTab("light")}
                      className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                        portionTab === "light"
                          ? "bg-sky-600 text-white shadow-sm"
                          : "text-gray-700 hover:text-gray-900"
                      }`}
                    >
                      Light ({editMode ? editKitchenIngredientsLight.length : (meal.kitchenIngredientsLight?.length || 0)})
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {editMode && portionTab === "light" && editKitchenIngredientsLight.length === 0 && editKitchenIngredients.length > 0 && (
                    <button
                      type="button"
                      onClick={handleCopyClassicToLight}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-bold text-sky-700 bg-sky-50 border border-sky-200 hover:bg-sky-100 rounded-lg transition-colors shadow-sm focus:outline-none"
                    >
                      <Copy className="w-3.5 h-3.5 mr-1" /> Copy from Classic
                    </button>
                  )}
                  {editMode && (
                    <button
                      type="button"
                      onClick={handleAddIngredient}
                      className={`inline-flex items-center px-3 py-1.5 text-sm font-bold rounded-lg transition-colors shadow-sm focus:outline-none ${
                        portionTab === "light"
                          ? "text-sky-700 bg-sky-50 border border-sky-200 hover:bg-sky-100"
                          : "text-purple-700 bg-purple-50 border border-purple-200 hover:bg-purple-100"
                      }`}
                    >
                      <Plus className="w-4 h-4 mr-1.5" /> Add Item
                    </button>
                  )}
                </div>
              </div>

              {/* View Mode Notices */}
              {!editMode && portionTab === "classic" && (!meal.kitchenIngredients || meal.kitchenIngredients.length === 0) && (
                <div className="bg-amber-50 border-b border-amber-200 p-3.5 flex items-start">
                  <Info className="h-4 w-4 text-amber-500 mt-0.5 mr-2.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800 leading-relaxed font-medium">
                    No Classic recipe configured yet.
                  </p>
                </div>
              )}
              {!editMode && portionTab === "light" && (!meal.kitchenIngredientsLight || meal.kitchenIngredientsLight.length === 0) && (
                <div className="bg-sky-50 border-b border-sky-200 p-3.5 flex items-start">
                  <Info className="h-4 w-4 text-sky-500 mt-0.5 mr-2.5 flex-shrink-0" />
                  <p className="text-xs text-sky-800 leading-relaxed font-medium">
                    No separate Light recipe configured. Kitchen requirements calculations will automatically fall back to the Classic recipe.
                  </p>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {editMode && <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">ID (key)</th>}
                      <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Ingredient Name</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-24">Qty</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-24">Unit</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Category</th>
                      {editMode && <th className="px-5 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider w-16">Act</th>}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {editMode ? (
                      currentList.length > 0 ? (
                        currentList.map((ing, i) => (
                          <tr key={i} className="hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-2.5">
                              <input type="text" placeholder="e.g. rice" className="w-full text-sm border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 px-3 py-1.5 border shadow-sm outline-none" value={ing.ingredientId} onChange={(e) => handleIngredientChange(i, 'ingredientId', e.target.value)} />
                            </td>
                            <td className="px-5 py-2.5">
                              <input type="text" placeholder="e.g. Basmati Rice" className="w-full text-sm font-medium border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 px-3 py-1.5 border shadow-sm outline-none" value={ing.displayName} onChange={(e) => handleIngredientChange(i, 'displayName', e.target.value)} />
                            </td>
                            <td className="px-5 py-2.5">
                              <input type="number" min="0" className="w-full text-sm font-bold border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 px-3 py-1.5 border shadow-sm outline-none" value={ing.quantity} onChange={(e) => handleIngredientChange(i, 'quantity', e.target.value)} />
                            </td>
                            <td className="px-5 py-2.5">
                              <input type="text" placeholder="g, ml, pc" className="w-full text-sm border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 px-3 py-1.5 border shadow-sm outline-none" value={ing.unit} onChange={(e) => handleIngredientChange(i, 'unit', e.target.value)} />
                            </td>
                            <td className="px-5 py-2.5">
                              <input type="text" placeholder="e.g. Grains" className="w-full text-sm border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 px-3 py-1.5 border shadow-sm outline-none" value={ing.category} onChange={(e) => handleIngredientChange(i, 'category', e.target.value)} />
                            </td>
                            <td className="px-5 py-2.5 text-right">
                              <button onClick={() => handleRemoveIngredient(i)} className="text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors p-1.5 focus:outline-none">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-5 py-10 text-center">
                            <div className="flex justify-center mb-3"><Utensils className="h-8 w-8 text-gray-300" /></div>
                            <p className="text-sm text-gray-500 font-medium">No ingredients added for {portionTab === 'classic' ? 'Classic' : 'Light'} portion yet.</p>
                          </td>
                        </tr>
                      )
                    ) : (
                      (portionTab === "classic" ? meal.kitchenIngredients : meal.kitchenIngredientsLight)?.length > 0 ? (
                        (portionTab === "classic" ? meal.kitchenIngredients : meal.kitchenIngredientsLight).map((ing: any, i: number) => (
                          <tr key={i} className="hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-3.5 text-sm font-bold text-gray-900">{ing.displayName}</td>
                            <td className="px-5 py-3.5 text-sm font-bold text-gray-700">{ing.quantity}</td>
                            <td className="px-5 py-3.5 text-sm text-gray-500">{ing.unit}</td>
                            <td className="px-5 py-3.5 text-sm text-gray-500">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                                {ing.category}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="px-5 py-10 text-center">
                            <div className="flex justify-center mb-3"><Info className="h-8 w-8 text-gray-300" /></div>
                            <p className="text-sm text-gray-500 font-medium">
                              No {portionTab === 'classic' ? 'Classic' : 'Light'} recipe configured.
                            </p>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            )}
          </div>
        ) : null}
      </div>
      
      {/* Footer Actions */}
      <div className="px-6 py-4 border-t border-gray-100 bg-white flex justify-end gap-3 rounded-b-2xl sticky bottom-0 z-10">
        {(editMode ? (
          <>
            <button onClick={() => setEditMode(false)} disabled={loading} className="px-4 py-2 text-sm font-bold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors flex items-center">
              <XCircle className="w-4 h-4 mr-2" /> Cancel
            </button>
            <button onClick={handleSave} disabled={loading} className="px-4 py-2 text-sm font-bold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors flex items-center">
              {loading ? <CircularProgress size={16} color="inherit" className="mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </button>
          </>
        ) : (
          isAdminUser && (
            <button onClick={() => {
              resetQuantities(meal);
              setEditName(meal.name);
              setEditKitchenIngredients(meal.kitchenIngredients || []);
              setEditKitchenIngredientsLight(meal.kitchenIngredientsLight || []);
              setEditMode(true);
            }} className="px-4 py-2 text-sm font-bold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors flex items-center">
              <Edit2 className="w-4 h-4 mr-2" /> Edit Meal
            </button>
          )
        ))}
      </div>
    </Dialog>
  );
};

export default MealDetailModal;
